export interface RetirementOptions {
  totalAsset: number;
  monthlyExpense: number;
  annualReturnRate: number;
  retirementAge: number;
}

export interface RetirementHistory {
  month: number;
  age: number;
  asset: number;
  monthlyReturn: number;
  withdrawal: number;
}

export interface RetirementResult {
  histories: RetirementHistory[];
  depletionAge: number | null;
  depletionMonth: number;
  isPerpetual: boolean;
  monthlyInterestIncome: number;
  requiredAssetForPerpetual: number;
}

export const formatNumber = new Intl.NumberFormat('ko-KR', {
  style: 'currency',
  currency: 'KRW',
  maximumFractionDigits: 0,
}).format;

export const calculateRetirement = (options: RetirementOptions): RetirementResult => {
  const { totalAsset, monthlyExpense, annualReturnRate, retirementAge } = options;
  const monthlyReturnRate = annualReturnRate / 100 / 12;

  const monthlyInterestIncome = totalAsset * monthlyReturnRate;

  const isPerpetual = monthlyInterestIncome >= monthlyExpense;

  const requiredAssetForPerpetual =
    monthlyReturnRate > 0 ? monthlyExpense / monthlyReturnRate : Infinity;

  const histories: RetirementHistory[] = [];
  let currentAsset = totalAsset;
  let month = 0;
  const maxMonths = 12 * 60;

  while (currentAsset > 0 && month < maxMonths) {
    const monthlyReturn = currentAsset * monthlyReturnRate;
    const withdrawal = Math.min(monthlyExpense, currentAsset + monthlyReturn);

    histories.push({
      month,
      age: retirementAge + Math.floor(month / 12),
      asset: currentAsset,
      monthlyReturn,
      withdrawal,
    });

    currentAsset = currentAsset + monthlyReturn - monthlyExpense;
    month++;
  }

  const depletionMonth = month;
  const depletionAge = isPerpetual ? null : retirementAge + Math.floor(depletionMonth / 12);

  return {
    histories,
    depletionAge,
    depletionMonth,
    isPerpetual,
    monthlyInterestIncome,
    requiredAssetForPerpetual,
  };
};
